import { FC, useEffect, useRef, useState } from 'react';
import { useToggle } from 'react-use';

import { AiBanner } from '@/shared/uikitV2/AiBanner';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { FileUploader, FileUploaderRef, UploadState } from '@/widgets/FileUploader';

import { BtnClose } from '../../components/BtnClose';
import { ConfirmClose } from '../../components/ConfirmClose';
import { useModalsStore } from '../../modals.store';
import { UploadFilesModalProps } from '../../types';

import { Actions, Body, Container, Header, LockContainer, StyledDrawer, Title } from './styled';

// TODO modal container RightSide
export const UploadFilesModal: FC<UploadFilesModalProps> = ({
  acceptFiles,
  allowedActions,
  fileItemType,
  isSmartImport,
  onUploaded,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'uploadFiles' });
  const { t: tp } = useTranslation('widgets/modals', { keyPrefix: 'uploadPhotos' });
  const { t: ts } = useTranslation('features/smartImport');
  const modals = useModalsStore();

  const uploaderRef = useRef<FileUploaderRef>(null);
  const [uploadState, setUploadState] = useState<UploadState | null>();
  const [showConfirmClose, toggleConfirm] = useToggle(false);

  const files = uploadState?.files;
  const uploading = Boolean(uploadState?.uploading);
  const isEmptyList = files?.length === 0;

  const onUserClose = () => {
    if (files?.length) {
      toggleConfirm(true);
    } else {
      modals.closeModal();
    }
  };

  const onConfirmClose = () => modals.closeModal();
  const onUpload = async () => {
    if (!uploaderRef.current) {
      return;
    }

    const res = await uploaderRef.current.upload();

    if (res.success) {
      modals.closeModal();
      onUploaded(res.filePaths);
    } else {
      console.error('Upload error');
    }
  };

  useEffect(() => {
    const shouldPrevent = files && files.length > 0;
    const handelBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = true;
    };

    if (shouldPrevent) {
      // TODO beforeunload
      window.addEventListener('beforeunload', handelBeforeUnload);
    }

    return () => {
      if (shouldPrevent) {
        window.removeEventListener('beforeunload', handelBeforeUnload);
      }
    };
  }, [files?.length]);

  const modalTitle =
    fileItemType === 'photoItem' ? tp('title') : isSmartImport ? ts('title') : t('title');

  return (
    <StyledDrawer width={600} footer={null} closable={false} onClose={onUserClose} open>
      <Container>
        <Header>
          <Title className="truncate" size={22} bold>
            {modalTitle}
          </Title>
          <BtnClose onClick={onUserClose} style={{ position: 'absolute', right: 24, top: 24 }} />
        </Header>
        <Body style={{ marginRight: 4 }} className="uikitScrollbar">
          {Boolean(isSmartImport) && (
            <AiBanner title={ts('aiBanner.bannerTitle')} description={ts('aiBanner.bannerText')} />
          )}
          <FileUploader
            acceptFiles={acceptFiles}
            allowedActions={allowedActions}
            fileItemType={fileItemType}
            onUpdate={setUploadState}
            ref={uploaderRef}
          />
        </Body>
        <Actions>
          <Button type="text" onClick={onUserClose} style={{ marginRight: 'auto' }}>
            {t('btnCancel')}
          </Button>
          <Button
            style={{ minWidth: 200 }}
            loading={uploading}
            disabled={isEmptyList}
            onClick={onUpload}
          >
            {t('btnConfirm')}
          </Button>
        </Actions>
        {showConfirmClose && <ConfirmClose onClose={toggleConfirm} onConfirm={onConfirmClose} />}
        {uploading && <LockContainer />}
      </Container>
    </StyledDrawer>
  );
};
